import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, AlertCircle, MapPin } from 'lucide-react';
import { referenceService } from '@/services/referenceService';
import { Localitate } from '@/types/reference.types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';

interface LocalitateAutocompleteProps {
  value?: number | null; // id_localitate
  onChange: (idLocalitate: number | null, numeFull?: string) => void;
  idJudet?: number | null; // Required: filter localities by county
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function LocalitateAutocomplete({
  value,
  onChange,
  idJudet,
  placeholder = 'Caută localitate...',
  disabled = false,
  className
}: LocalitateAutocompleteProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localitati, setLocalitati] = useState<Localitate[]>([]);
  const [selectedLocalitate, setSelectedLocalitate] = useState<Localitate | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load localitati when idJudet changes or on search
  useEffect(() => {
    if (idJudet) {
      loadLocalitati(idJudet);
    } else {
      setLocalitati([]);
      setSelectedLocalitate(null);
    }
  }, [idJudet]);

  // Set initial selected localitate based on value prop
  useEffect(() => {
    if (value && localitati.length > 0) {
      const localitate = localitati.find(l => l.id_localitate === value);
      if (localitate) {
        setSelectedLocalitate(localitate);
        setSearchTerm('');
      }
    } else if (!value) {
      setSelectedLocalitate(null);
      setSearchTerm('');
    }
  }, [value, localitati]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLocalitati = async (judetId: number, search?: string) => {
    setIsLoading(true);
    try {
      const data = await referenceService.getLocalitati(judetId, search);
      setLocalitati(data);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca localitățile. Vă rugăm încercați din nou.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  const handleSearch = (term: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!idJudet) return;

    if (term.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        loadLocalitati(idJudet, term);
      }, 300); // 300ms debounce
    } else if (term.trim().length === 0) {
      // Load all localities when search is cleared
      loadLocalitati(idJudet);
    }
  };

  // Filter localitati based on search term (client-side filtering)
  const filteredLocalitati = useMemo(() => {
    if (!searchTerm.trim()) {
      return localitati;
    }

    const term = searchTerm.toLowerCase().trim();
    return localitati.filter(localitate =>
      localitate.nume.toLowerCase().includes(term)
    );
  }, [localitati, searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing
    if (selectedLocalitate) {
      setSelectedLocalitate(null);
      onChange(null);
    }

    // Trigger server-side search
    handleSearch(value);
  };

  const handleSelectLocalitate = (localitate: Localitate) => {
    setSelectedLocalitate(localitate);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(localitate.id_localitate, localitate.nume);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSelectedLocalitate(null);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(null);
    if (idJudet) {
      loadLocalitati(idJudet); // Reload all localities
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredLocalitati.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredLocalitati.length) {
          handleSelectLocalitate(filteredLocalitati[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [highlightedIndex]);

  const displayValue = selectedLocalitate ? selectedLocalitate.nume : searchTerm;

  const isFieldDisabled = disabled || !idJudet;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => idJudet && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={idJudet ? placeholder : 'Selectați județul mai întâi'}
          disabled={isFieldDisabled || isLoading}
          className={cn(
            'pl-10 pr-10',
            selectedLocalitate && 'font-medium'
          )}
        />
        {selectedLocalitate && !isFieldDisabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !isFieldDisabled && (
        <div
          ref={dropdownRef}
         // className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[300px] overflow-y-auto"
          className=""
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Se încarcă localități...
            </div>
          ) : !idJudet ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Selectați județul mai întâi
              </p>
            </div>
          ) : filteredLocalitati.length === 0 ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit localități' : 'Nu sunt localități disponibile'}
              </p>
              {searchTerm.length > 0 && searchTerm.length < 2 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Introduceți cel puțin 2 caractere pentru căutare
                </p>
              )}
            </div>
          ) : (
            <>
              {filteredLocalitati.length > 50 && (
                <div className="px-3 py-2 text-xs text-muted-foreground bg-muted border-b">
                  {filteredLocalitati.length} localități găsite. Folosiți căutarea pentru a filtra rezultatele.
                </div>
              )}
              {filteredLocalitati.slice(0, 100).map((localitate, index) => (
                <div
                  key={localitate.id_localitate}
                  className={cn(
                    'px-3 py-2 cursor-pointer text-sm transition-colors text-foreground',
                    'hover:bg-accent hover:text-accent-foreground',
                    highlightedIndex === index && 'bg-accent text-accent-foreground',
                    selectedLocalitate?.id_localitate === localitate.id_localitate && 'bg-primary/10 font-medium'
                  )}
                  onClick={() => handleSelectLocalitate(localitate)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">
                        {localitate.nume}
                      </div>
                      {localitate.cod_siruta && (
                        <div className="text-xs text-foreground/60">
                          SIRUTA: {localitate.cod_siruta}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredLocalitati.length > 100 && (
                <div className="px-3 py-2 text-xs text-center text-muted-foreground bg-muted border-t">
                  Afișate primele 100 din {filteredLocalitati.length} rezultate
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
